import React, { useCallback } from "react";

import {
  Box,
  Button,
  Center,
  HStack,
  Image,
  Stack,
  Text,
  useDisclosure
} from "@chakra-ui/react";

import { Link } from "react-router-dom";

import { IBankConnection } from "@/jenesys-types/jenesys-entities/config";

import Barrier from "@/components/common/permission/Barrier";
import { AddBank } from "@/components/features/reconciliation/AddBank";
import { ConnectBank } from "@/routes/lazyFeatures";
import { bankReconPlatforms } from "@/util/helpers";

interface BankReconCardProps {
  platform: (typeof bankReconPlatforms)[0];
  connections: Array<IBankConnection>;
}

export const BankReconCard: React.FC<BankReconCardProps> = ({
  platform,
  connections
}) => {
  const chooseBankDisclosure = useDisclosure();
  const addBankDisclosure = useDisclosure();
  const isPlatformAvailable = !platform.isDisabled;

  const platformConnections = connections.filter(
    (connection) => connection.status === platform.status
  );

  const isConnected = !!platformConnections.length;

  const handleConnectButton = useCallback(() => {
    if (platform.status === "MANUAL") {
      addBankDisclosure.onOpen();
    } else {
      chooseBankDisclosure.onOpen();
    }
  }, [chooseBankDisclosure, addBankDisclosure, platform.status]);

  return (
    <Stack
      px={4}
      opacity={isPlatformAvailable ? 1 : 0.4}
      py={4}
      spacing={6}
      border="1px solid"
      rounded={6}
      borderColor="blackAlpha.200"
    >
      <Stack
        w="full"
        spacing={4}
        justify="space-between"
        align={{ base: "flex-start", lg: "center" }}
        direction={{ base: "column", lg: "row" }}
      >
        <HStack spacing={4} align="flex-start">
          <Image
            alt={platform.name}
            src={platform.imageLink}
            width={9}
            height={9}
          />
          <Stack spacing={1}>
            <Text fontSize={20} fontWeight={600}>
              {platform.displayName}
            </Text>

            <Text maxW={480} fontSize={12} fontWeight={400}>
              {platform.description}
            </Text>
            {isConnected && (
              <Text fontSize={12} fontWeight={400}>
                {platformConnections.length} Bank
                {platformConnections.length > 1 && `s`}
                &nbsp;connected
              </Text>
            )}
          </Stack>
        </HStack>

        {isPlatformAvailable && (
          <Box>
            {!isConnected ? (
              <Barrier show when="company.connections.bank-reconciliation">
                <Button
                  onClick={handleConnectButton}
                  h={9}
                  colorScheme="primary"
                  minW={130}
                  size="sm"
                >
                  Connect
                </Button>
              </Barrier>
            ) : (
              <Barrier show when="company.connections.bank-reconciliation">
                <Button
                  as={Link}
                  to={`${platform.category}/${platform.name}`}
                  h={9}
                  w={95}
                  size="sm"
                  variant="outline"
                >
                  Manage
                </Button>
              </Barrier>
            )}
          </Box>
        )}

        {!isPlatformAvailable && (
          <Center>
            <Text
              fontStyle="italic"
              fontSize={12}
              color="primary.300"
              maxW={["full", "full", "full", 250]}
              fontWeight={400}
            >
              Coming soon...
            </Text>
          </Center>
        )}
      </Stack>

      {chooseBankDisclosure.isOpen && <ConnectBank {...chooseBankDisclosure} />}
      {addBankDisclosure.isOpen && <AddBank {...addBankDisclosure} />}
    </Stack>
  );
};
